import React, { FC, useEffect, useRef, useState } from 'react'
import { DatePicker } from 'antd'
import { RangePickerProps } from 'antd/es/date-picker'
import RangePickerFooter from 'UI/RangePickerFooter/RangePickerFooter'
import { Dayjs } from 'dayjs'
import { RangeValueType } from 'rc-picker/lib/PickerInput/RangePicker'
import styles from './RangePicker.scss'
import classNames from 'classnames'
import { isLessThanOneYear } from './utils'

type ICustomRangePickerProps = RangePickerProps & {
  submitText: string
  onSubmit: (value: RangeValueType<Dayjs> | null) => void
}

const { RangePicker } = DatePicker

const CustomRangePicker: FC<ICustomRangePickerProps> = ({ submitText, onSubmit, ...props }) => {
  const [open, setOpen] = useState(false)
  const [value, setValue] = useState<RangeValueType<Dayjs> | null | undefined>(null)

  const [oldValue, setOldValue] = useState<RangeValueType<Dayjs> | null | undefined>(null)

  const pickerRef = useRef<HTMLDivElement>(null)

  const onRangeChange = (dates: null | (Dayjs | null)[]) => {
    if (dates) {
      setValue(dates as RangeValueType<Dayjs>)
      if (!isLessThanOneYear(dates as RangeValueType<Dayjs>)) {
        setOpen(false)
        onSubmit(dates as RangeValueType<Dayjs>)
      }
    } else {
      setValue(null)
      setOpen(false)
      onSubmit(null)
    }
  }

  const onClick = () => {
    setOpen(true)
    setOldValue(value)
    setValue(null)
  }

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const pickerElement = pickerRef.current
      const popupElement = document.querySelector('.ant-picker-dropdown')

      if (
        pickerElement &&
        !pickerElement.contains(event.target as Node) &&
        popupElement &&
        !popupElement.contains(event.target as Node)
      ) {
        setOpen(false)
        setValue(oldValue)
      }
    }

    if (open) {
      document.addEventListener('mousedown', handleClickOutside)
    } else {
      document.removeEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [open, oldValue])

  return (
    <div ref={pickerRef}>
      <RangePicker
        {...props}
        open={open}
        value={value}
        popupClassName={classNames(styles.popup, value && styles.popupNotSelectable)}
        onChange={onRangeChange}
        onClick={onClick}
        renderExtraFooter={
          value && isLessThanOneYear(value)
            ? () => {
                return (
                  <RangePickerFooter
                    text={submitText}
                    onSubmit={() => {
                      onSubmit(value)
                      setOpen(false)
                    }}
                  />
                )
              }
            : undefined
        }
      />
    </div>
  )
}

export default CustomRangePicker
